import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Check, Zap, MessageSquare, Star, Sparkles, Info, X } from 'lucide-react';

const ForIndividuals = () => {
  const [showInviteBoostsInfo, setShowInviteBoostsInfo] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-gray-700 hover:text-blue-600 transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-semibold">Back to Home</span>
            </Link>
            <img 
              src="https://customer-assets.emergentagent.com/job_fltr-discovery/artifacts/eyyllr9s_zugf049p_%7B57BB1C3A-D64A-4745-A035-1D9438C12699%7D.png" 
              alt="FltrLnk Logo" 
              className="h-8"
            />
          </div>
        </div>
      </nav>

      {/* What is FltrLnk Section */}
      <section className="py-20 px-4 bg-white" data-testid="what-is-fltrlnk-section">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block bg-blue-100 text-blue-700 px-6 py-2 rounded-full mb-6 text-sm font-bold">
            WHAT IS FLTRLNK?
          </div>
          <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-6">
            The Next-Gen Discovery Platform for Everybody
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            FltrLnk is a next-generation user discovery platform where people find each other, businesses, opportunities, and connections through swipe-based interfaces and GPS-enabled maps. For People, it's a brand-new channel to meet new people and discover your surroundings for free — from anywhere at anytime.
          </p>
        </div>
      </section>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-full mb-6 text-sm font-bold">
            FOR INDIVIDUAL USERS
          </div>
          <h1 className="text-5xl lg:text-6xl font-black text-gray-900 mb-6">
            FltrLite for <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Individuals</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            FltrLite is a premium subscription service that authenticates users and helps maximize their visibility on the platform. Stand out from the crowd with verified status and enhanced discovery features.
          </p>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            
            {/* FltrLite Starter */}
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border-2 border-blue-200 hover:border-blue-500 transition-colors relative">
              <div className="absolute top-4 right-4 bg-gradient-to-r from-amber-400 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                MOST POPULAR
              </div>
              
              <div className="bg-gradient-to-r from-blue-600 to-blue-600 p-6 text-white">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Shield className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">FltrLite Starter</h3>
                    <p className="text-blue-100 text-sm">Perfect for getting started</p>
                  </div>
                </div>
              </div>
              
              <div className="p-8">
                <div className="text-center mb-8">
                  <div className="text-5xl font-black text-gray-900">$1.99</div>
                  <p className="text-gray-500 font-semibold mt-2">One-Time Payment</p>
                  <div className="inline-block bg-blue-100 text-blue-700 px-4 py-1 rounded-full text-sm font-bold mt-3">
                    LIFETIME ACCESS
                  </div>
                </div>

                <ul className="space-y-4 mb-8">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900">Lifetime Profile Verification</span>
                      <p className="text-gray-600 text-sm">Get verified once, stay verified forever</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900">Verification Badge</span>
                      <p className="text-gray-600 text-sm">Display your verified status on your profile</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900">Priority Visibility in Algorithms</span>
                      <p className="text-gray-600 text-sm">Get discovered first in search and discovery feeds</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900">Early Access to New Features</span>
                      <p className="text-gray-600 text-sm">Be first to try new FltrLnk features as they roll out</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900">Future Monetization Eligibility</span>
                      <p className="text-gray-600 text-sm">Become eligible for upcoming ways to earn on the platform</p>
                    </div>
                  </li>
                </ul>

                <p className="text-xs text-gray-500 text-center italic">*Only available through in app purchase</p>
              </div>
            </div>

            {/* FltrLite Basic */}
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border-2 border-purple-300 hover:border-purple-500 transition-colors">
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-6 text-white">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Sparkles className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">FltrLite Basic</h3>
                    <p className="text-purple-100 text-sm">Maximum visibility & engagement</p>
                  </div>
                </div>
              </div>
              
              <div className="p-8">
                <div className="text-center mb-8">
                  <div className="text-5xl font-black text-gray-900">$9.99</div>
                  <p className="text-gray-500 font-semibold mt-2">Per Month</p>
                  <div className="inline-block bg-purple-100 text-purple-700 px-4 py-1 rounded-full text-sm font-bold mt-3">
                    CANCEL ANYTIME
                  </div>
                </div>

                <ul className="space-y-4 mb-8">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900">Profile Verification</span>
                      <p className="text-gray-600 text-sm">Verified badge displayed on your profile</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900">100 Monthly Business Chats</span>
                      <p className="text-gray-600 text-sm">Connect with businesses directly each month</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900">5 Monthly User Boost Packs</span>
                      <p className="text-gray-600 text-sm">Each pack includes 8 different user boosts</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900">Priority Visibility in Algorithms</span>
                      <p className="text-gray-600 text-sm">Get discovered first in search and feeds</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900">Early Access to New Features</span>
                      <p className="text-gray-600 text-sm">Be first to try new FltrLnk features as they roll out</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900">Future Monetization Eligibility</span>
                      <p className="text-gray-600 text-sm">Become eligible for upcoming ways to earn on the platform</p>
                    </div>
                  </li>
                </ul>

                <p className="text-xs text-gray-500 text-center italic">*Only available through in app purchase</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* User Boost Types Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-3 mb-3">
              <h2 className="text-3xl font-bold text-gray-900">8 User Boost Types Included</h2>
              <button 
                onClick={() => setShowInviteBoostsInfo(true)}
                className="text-purple-600 hover:text-purple-800 transition-colors"
                data-testid="boost-types-info-button"
              >
                <Info className="w-6 h-6" />
              </button>
            </div>
            <p className="text-gray-600">Each User Boost Pack includes all 8 boost types to maximize your visibility</p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-3">
            <span className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full font-semibold border border-blue-300">FltrBoosts</span>
            <span className="bg-green-100 text-green-800 px-4 py-2 rounded-full font-semibold border border-green-300">LNKBoosts</span>
            <span className="bg-purple-100 text-purple-800 px-4 py-2 rounded-full font-semibold border border-purple-300">MatchBoosts</span>
            <span className="bg-orange-100 text-orange-800 px-4 py-2 rounded-full font-semibold border border-orange-300">GPSBoosts</span>
            <span className="bg-teal-100 text-teal-800 px-4 py-2 rounded-full font-semibold border border-teal-300">LocationBoosts</span>
            <span className="bg-indigo-100 text-indigo-800 px-4 py-2 rounded-full font-semibold border border-indigo-300">UserBoosts</span>
            <span className="bg-pink-100 text-pink-800 px-4 py-2 rounded-full font-semibold border border-pink-300">SearchBoosts</span>
            <span className="bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full font-semibold border border-yellow-300">SpotlightBoosts</span>
          </div>
        </div>
      </section>

      {/* Why FltrLite Section */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Why Choose FltrLite?</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-2xl shadow-lg text-center">
              <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Build Trust</h3>
              <p className="text-gray-600 text-sm">Verification badges show others you're a real, authenticated user</p>
            </div>
            
            <div className="bg-white p-6 rounded-2xl shadow-lg text-center">
              <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Zap className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Get Discovered</h3>
              <p className="text-gray-600 text-sm">Priority placement means more people find and connect with you</p>
            </div>
            
            <div className="bg-white p-6 rounded-2xl shadow-lg text-center">
              <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Star className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Stand Out</h3>
              <p className="text-gray-600 text-sm">User boosts help you appear in more feeds and searches</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-4xl font-black text-white mb-6">FltrLnk</h2>
          <div className="flex justify-center gap-6 mb-6 flex-wrap">
            <Link to="/" className="text-gray-400 hover:text-white transition-colors">Home</Link>
            <Link to="/4individuals" className="text-gray-400 hover:text-white transition-colors">4Individuals</Link>
            <Link to="/4business" className="text-gray-400 hover:text-white transition-colors">4Business</Link>
            <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">Terms</Link>
            <Link to="/contact" className="text-gray-400 hover:text-white transition-colors">Contact</Link>
          </div>
          <p className="text-gray-500 text-sm">
            © 2025 FltrLnk. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Invite Boosts Info Modal */}
      {showInviteBoostsInfo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowInviteBoostsInfo(false)}>
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">8 User Boost Types</h3>
              <button onClick={() => setShowInviteBoostsInfo(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-gray-700 mb-6">
              Each User Boost Pack contains 1 boost for each of these 8 boost types to maximize your profile visibility:
            </p>
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-600">
                <h4 className="font-bold text-blue-800 mb-1">1. FltrBoosts</h4>
                <p className="text-gray-700 text-sm">Boosts your profile visibility within the Fltr Category you are in. Lasts 1 hour.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                <h4 className="font-bold text-green-800 mb-1">2. LNKBoosts</h4>
                <p className="text-gray-700 text-sm">Boosts your profile visibility to other users based on profile similarities to users you are LNKD with. Lasts 1 hour.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
                <h4 className="font-bold text-purple-800 mb-1">3. MatchBoosts</h4>
                <p className="text-gray-700 text-sm">Boosts your profile visibility based on profile similarities to other users. Lasts 1 hour.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-500">
                <h4 className="font-bold text-orange-800 mb-1">4. GPSBoosts</h4>
                <p className="text-gray-700 text-sm">Boosts your profile visibility based on your current location to other users within a 100-mile radius on the map. Lasts 1 hour.</p>
              </div>
              <div className="bg-teal-50 p-4 rounded-lg border-l-4 border-teal-500">
                <h4 className="font-bold text-teal-800 mb-1">5. LocationBoosts</h4>
                <p className="text-gray-700 text-sm">Boosts your profile visibility towards users in the same location as you on the Map or Fltr Screen. Lasts 1 hour.</p>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-500">
                <h4 className="font-bold text-indigo-800 mb-1">6. UserBoosts</h4>
                <p className="text-gray-700 text-sm">Boosts your profile visibility based on your user type or niche when another user searches that type — whether Fltring, swiping, or discovering on the map. Lasts 1 hour.</p>
              </div>
              <div className="bg-pink-50 p-4 rounded-lg border-l-4 border-pink-500">
                <h4 className="font-bold text-pink-800 mb-1">7. SearchBoosts</h4>
                <p className="text-gray-700 text-sm">Boosts your profile visibility based on keyword search relevance that correlates to your profile information. Lasts 1 hour.</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
                <h4 className="font-bold text-yellow-800 mb-1">8. SpotlightBoosts ⭐</h4>
                <p className="text-gray-700 text-sm">The ultimate boost — places your profile at the top of all other boost algorithms. SpotlightBoosts supersede all other boosts and combine the functions of all 7 boost types into one powerful visibility enhancement. Lasts 1 hour.</p>
              </div>
            </div>
            <button onClick={() => setShowInviteBoostsInfo(false)} className="w-full bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700 mt-6">
              Got it!
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForIndividuals;
